<template>
  <div class="min-h-screen bg-slate-50 font-sans">
    <!-- Hero Section -->
    <div class="relative bg-primary text-white pt-20 pb-32 overflow-hidden">
      <!-- Background Pattern/Gradient Overlay -->
      <div
        class="absolute inset-0 opacity-20 bg-[radial-gradient(var(--color-secondary)_1px,transparent_1px)] [background-size:20px_20px]">
      </div>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
        <div
          class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 text-primary text-sm font-medium mb-8 backdrop-blur-sm">
          <UIcon name="i-heroicons-shield-check" />
          <span>Secure & Audit-Ready</span>
        </div>

        <h1 class="text-5xl md:text-7xl font-extrabold tracking-tight mb-6 leading-tight">
          Protect Your Wealth <br />
          <span class="text-secondary">Before Disaster Strikes.</span>
        </h1>

        <p class="mt-6 max-w-2xl mx-auto text-xl text-slate-300">
          Insurance adjusters look for reasons to lower your claim. Don't let them. Create a professional,
          audit-ready home inventory in under 20 minutes -- without your data ever leaving your device.
        </p>

        <div class="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
          <template v-if="inventoryStore.reports.length === 0">
            <UButton size="xl" color="secondary" variant="solid"
              class="px-8 py-4 text-white font-bold text-lg shadow-[0_0_20px_rgba(var(--color-secondary),0.3)] hover:shadow-[0_0_30px_rgba(var(--color-secondary),0.5)] transition-all transform hover:-translate-y-1"
              :loading="inventoryStore.isLoading" @click="createNewReport">
              <span>Start Inventory</span>
              <template #trailing>
                <UIcon name="i-heroicons-arrow-right" />
              </template>
            </UButton>
          </template>
          <template v-else>
            <UButton size="xl" color="neutral" variant="outline" @click="scrollToReports">
              View Existing Reports
            </UButton>
          </template>
        </div>

        <div class="mt-4 text-center">
          <small class="text-slate-300 text-sm">ClaimReady is a documentation tool for personal record-keeping, not a
            legal
            appraisal service.</small>
        </div>
      </div>
    </div>

    <!-- Value Props / Feature Grid -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-20" id="features">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <!-- Card 1 -->
        <div class="bg-white rounded-2xl p-8 shadow-xl border border-slate-100 hover:shadow-2xl transition-shadow">
          <div class="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center mb-6">
            <UIcon name="i-heroicons-document-check" class="text-3xl text-secondary" />
          </div>
          <h3 class="text-xl font-bold text-slate-900 mb-3">True Zero-Knowledge</h3>
          <p class="text-slate-600 leading-relaxed">
            Unlike other apps, we don't have a database of your valuables. Your photos of jewelry, electronics, and
            collectibles are processed entirely on your machine. We can't leak what we don't have!
          </p>
        </div>

        <!-- Card 2 -->
        <div class="bg-white rounded-2xl p-8 shadow-xl border border-slate-100 hover:shadow-2xl transition-shadow">
          <div class="w-14 h-14 bg-sky-50 rounded-xl flex items-center justify-center mb-6">
            <UIcon name="i-heroicons-bolt" class="text-3xl text-secondary" />
          </div>
          <h3 class="text-xl font-bold text-slate-900 mb-3">Forensic-Ready PDF</h3>
          <p class="text-slate-600 leading-relaxed">
            Generate a comprehensive, high-resolution report formatted specifically for insurance adjusters with just
            one
            click. Insurance adjusters will love it, and you're likely to get your payout faster.
          </p>
        </div>

        <!-- Card 3 -->
        <div class="bg-white rounded-2xl p-8 shadow-xl border border-slate-100 hover:shadow-2xl transition-shadow">
          <div class="w-14 h-14 bg-emerald-50 rounded-xl flex items-center justify-center mb-6">
            <UIcon name="i-heroicons-lock-closed" class="text-3xl text-emerald-600" />
          </div>
          <h3 class="text-xl font-bold text-slate-900 mb-3">One-Time Tool</h3>
          <p class="text-slate-600 leading-relaxed">
            No accounts, no subscriptions, and no "forgotten password" emails. Pay once ($6.99) and get your report
            instantly. We wanted to make this tool as simple and as accessible as possible.
          </p>
        </div>
      </div>
    </div>

    <!-- Social Proof / Statistic -->
    <div class="py-24 bg-slate-50 border-b border-slate-200">
      <div class="max-w-4xl mx-auto px-4 text-center">
        <h2 class="text-3xl font-bold text-slate-900 mb-6">
          Avoid the "Payout Gap"
        </h2>
        <div class="p-6 bg-amber-50 rounded-lg border border-amber-100 inline-block text-left max-w-2xl">
          <p class="text-slate-800 font-medium text-lg">
            To an insurance adjuster, a 'toaster' is worth $5. A 'Breville 4-Slice Smart Toaster' is worth $180. Without
            proof, you may lose $175 on a single item. Now multiply that by every item in your kitchen.
          </p>
        </div>
      </div>
    </div>

    <!-- How It Works Section -->
    <div class="py-24 bg-white text-slate-900 border-b border-slate-200" id="howItWorks">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-primary">How It Works</h2>
          <p class="text-slate-600 max-w-2xl mx-auto">Create a professional home inventory in three simple steps.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <!-- Step 1 -->
          <div class="text-center p-6">
            <div class="w-20 h-20 mx-auto bg-indigo-50 rounded-full flex items-center justify-center mb-6">
              <UIcon name="i-heroicons-computer-desktop" class="text-4xl text-primary" />
            </div>
            <h3 class="text-xl font-bold mb-3">1. Rapid Entry</h3>
            <p class="text-slate-600">
              Start on your laptop. List your high-value items in minutes using our rapid-entry interface. No account
              creation required.
            </p>
          </div>

          <!-- Step 2 -->
          <div class="text-center p-6">
            <div class="w-20 h-20 mx-auto bg-sky-50 rounded-full flex items-center justify-center mb-6">
              <UIcon name="i-heroicons-photo" class="text-4xl text-secondary" />
            </div>
            <h3 class="text-xl font-bold mb-3">2. Add Photos</h3>
            <p class="text-slate-600">
              Upload detailed photos directly from your computer. Visual proof is the key to getting your full payout.
            </p>
          </div>

          <!-- Step 3 -->
          <div class="text-center p-6">
            <div class="w-20 h-20 mx-auto bg-emerald-50 rounded-full flex items-center justify-center mb-6">
              <UIcon name="i-heroicons-document-check" class="text-4xl text-emerald-600" />
            </div>
            <h3 class="text-xl font-bold mb-3">3. Download & Secure</h3>
            <p class="text-slate-600">
              Generate your official inventory report. Pay a one-time fee of $6.99 to receive your audit-ready PDF.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Comparison Table -->
    <div class="py-24 bg-white border-b border-slate-200" id="advantage">
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl font-bold text-center text-primary mb-12">The ClaimReady Advantage</h2>
        <div class="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table class="min-w-full divide-y divide-gray-300">
            <thead class="bg-slate-50">
              <tr>
                <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Feature</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-bold text-primary">ClaimReady</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Cloud Apps (Sortly, HomeZada)</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Spreadsheets / Free</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
              <tr>
                <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">Data Storage</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm font-bold text-secondary">Local Device Only</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">Cloud Servers</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">Cloud Drive / Local</td>
              </tr>
              <tr>
                <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">Privacy (Who sees photos?)</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm font-bold text-secondary">Only You</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">App Employees & AI</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">You</td>
              </tr>
              <tr>
                <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">Security Risk</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm font-bold text-secondary">None (No Database)</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">High (Breaches)</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">Low</td>
              </tr>
              <tr>
                <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">Export for Adjusters</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm font-bold text-secondary">Forensic-Ready PDF</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">Generic PDF / CSV</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">Manual Formatting</td>
              </tr>
              <tr>
                <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">Price</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm font-bold text-secondary">$6.99 (One-time)</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">$5-$18 / month</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">Free</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- FAQ / Education Section -->
    <div class="py-24 bg-white" id="faq">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <!-- Image Column -->
          <div class="relative">
            <div class="absolute inset-0 bg-secondary rounded-2xl transform rotate-3 scale-95 opacity-20"></div>
            <img src="/family-meeting.png" alt="Family reviewing claim with insurance adjuster"
              class="relative rounded-2xl shadow-2xl z-10 w-full" />
          </div>

          <!-- Content Column -->
          <div>
            <h2 class="text-3xl font-bold text-primary mb-8">Common Questions</h2>

            <div class="space-y-8">
              <div>
                <h3 class="font-bold text-lg text-slate-900 mb-2">Why do I need this report?</h3>
                <p class="text-slate-600">
                  When you file a claim, the burden of proof is on <i>you</i>. Without a detailed inventory, insurance
                  companies may deny coverage for items you can't prove you owned. ClaimReady provides a detailed
                  inventory
                  report that can help you prove your claim.
                </p>
              </div>

              <div>
                <h3 class="font-bold text-lg text-slate-900 mb-2">Can't I just take a video?</h3>
                <p class="text-slate-600">
                  Videos are better than nothing, but they often lack the detail (serial numbers, purchase dates)
                  required
                  for full reimbursement. Our reports are structured specifically for claims adjusters.
                </p>
              </div>

              <div>
                <h3 class="font-bold text-lg text-slate-900 mb-2">What do I get explicitly?</h3>
                <p class="text-slate-600 mb-2">
                  A professional, organized PDF manifest. It compiles your items, values, and photos into a clean
                  document that insurance adjusters respect.
                </p>
              </div>

              <div>
                <h3 class="font-bold text-lg text-slate-900 mb-2">Do I need an account?</h3>
                <p class="text-slate-600 mb-2">
                  No. We believe your data belongs to you. We don't store your inventory, so you don't need a login.
                  Just create, pay, and download.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Existing Reports Section (Hidden if empty) -->
    <div v-if="inventoryStore.reports.length > 0" id="reports-section"
      class="py-20 bg-slate-50 border-t border-slate-200">
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-end mb-8">
          <div>
            <h2 class="text-3xl font-bold text-primary">Your Dashboard</h2>
            <p class="text-slate-500 mt-1">Pick up where you left off.</p>
          </div>
          <UButton icon="i-heroicons-plus" color="primary" variant="soft" label="New Report" @click="createNewReport" />
        </div>

        <div class="grid grid-cols-1 gap-4">
          <UCard v-for="report in inventoryStore.reports" :key="report.id"
            class="group hover:ring-2 hover:ring-secondary hover:shadow-lg transition-all cursor-pointer border-slate-200"
            @click="openReport(report.id)">
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-4">
                <div
                  class="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-primary group-hover:text-white transition-colors">
                  <UIcon name="i-heroicons-folder" class="text-2xl" />
                </div>
                <div>
                  <h3 class="font-bold text-lg text-slate-900 group-hover:text-primary transition-colors">{{
                    report.name
                  }}</h3>
                  <div class="flex gap-3 text-sm text-slate-500 mt-1">
                    <span class="flex items-center gap-1">
                      <UIcon name="i-heroicons-square-3-stack-3d" />
                      {{ getItemCount(report) }} Items
                    </span>
                    <span class="text-slate-300">•</span>
                    <span class="flex items-center gap-1 text-emerald-600 font-medium">
                      <UIcon name="i-heroicons-currency-dollar" />
                      {{ formattedValue(report) }}
                    </span>
                    <span v-if="report.completedOn" class="text-slate-300">•</span>
                    <UTooltip v-if="report.completedOn" text="Completed On">
                      <span class="flex items-center gap-1">
                        <UIcon name="i-heroicons-calendar" />
                        {{ new Date(report.completedOn).toLocaleDateString() }}
                      </span>
                    </UTooltip>
                  </div>

                  <div v-if="report.completedOn" class="mt-2">
                    <UBadge label="Completed" color="info" variant="subtle" size="sm" icon="i-heroicons-lock-closed" />
                  </div>
                </div>
              </div>
              <UIcon name="i-heroicons-chevron-right"
                class="text-gray-300 group-hover:text-secondary text-xl transition-colors transform group-hover:translate-x-1" />
            </div>
          </UCard>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { useInventoryStore } from '~/stores/inventory'

useSeoMeta({
  title: 'ClaimReady - Secure Home Inventory',
  ogTitle: 'ClaimReady - Secure Home Inventory',
  description: 'Prove your claim with a forensic-ready home inventory report. Zero-knowledge, secure, and accepted by all major insurance carriers.',
  ogDescription: 'Prove your claim with a forensic-ready home inventory report. Zero-knowledge, secure, and accepted by all major insurance carriers.',
  ogImage: 'https://getclaimready.app/cover.png',
  twitterCard: 'summary_large_image',
})

useSchemaOrg([
  defineWebSite({
    name: 'ClaimReady',
  }),
  defineWebPage({
    name: 'Home',
  })
])

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Why do I need this report?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "When you file a claim, the burden of proof is on you. Without a detailed inventory, insurance companies may deny coverage for items you can't prove you owned. ClaimReady provides a detailed inventory report that can help you prove your claim."
            }
          },
          {
            "@type": "Question",
            "name": "Can't I just take a video?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Videos are better than nothing, but they often lack the detail (serial numbers, purchase dates) required for full reimbursement. Our reports are structured specifically for claims adjusters."
            }
          },
          {
            "@type": "Question",
            "name": "What do I get explicitly?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "A professional, organized PDF manifest. It compiles your items, values, and photos into a clean document that insurance adjusters respect."
            }
          },
          {
            "@type": "Question",
            "name": "Do I need an account?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No. We believe your data belongs to you. We don't store your inventory, so you don't need a login. Just create, pay, and download."
            }
          }
        ]
      })
    }
  ]
})

definePageMeta({
  layout: 'home'
})

const inventoryStore = useInventoryStore()

// Init persistence on mount
onMounted(async () => {
  await inventoryStore.init()
})

const createNewReport = async () => {
  const reportId = await inventoryStore.createReport()
  navigateTo(`/reports/${reportId}`)
}

const openReport = (id: string) => {
  navigateTo(`/reports/${id}`)
}

const scrollToReports = () => {
  const el = document.getElementById('reports-section')
  if (el) el.scrollIntoView({ behavior: 'smooth' })
}

const formattedValue = (report: any) => {
  const total = report.rooms.reduce((acc: number, r: any) => acc + r.items.reduce((s: number, i: any) => s + Number(i.value), 0), 0)
  return total.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

const getItemCount = (report: any) => {
  return report.rooms.reduce((total: number, room: any) => total + room.items.filter((i: any) => {
    const hasName = i.name && i.name.trim().length > 0;
    const hasValue = i.value && Number(i.value) > 0;
    const hasPhotos = i.photos && i.photos.length > 0;
    return hasName || hasValue || hasPhotos;
  }).length, 0)
}
</script>
